import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ChatBlock } from '../../models/help-chat.models';
import { InlineSegment, inlineBoldSegments } from './inline-bold';

/**
 * Native Karten-Darstellung strukturierter Chatbot-Antworten (Phase C). Rendert die vom
 * Backend gelieferten {@link ChatBlock}s als Sektions-, KPI-, Ranking-, Besetzungs- und
 * Feld-Karten — ausschliesslich im vorhandenen Blau-/Neutral-Schema. Dringlichkeit ueber
 * gefuelltes-vs-umrandetes blaues Chip + ⚠-Glyph, KEINE zweite Farbe.
 *
 * Zeilen mit `action` sind klickbar und loesen ueber {@link rowAction} eine Folgefrage aus
 * (z. B. Klick auf einen Mitarbeiter → „Profil von …").
 */
@Component({
  selector: 'app-chat-blocks',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="cb">
      <ng-container *ngFor="let b of blocks">
        <!-- SECTION -->
        <section *ngIf="b.type === 'section'" class="cb-card">
          <header class="cb-head" *ngIf="b.title">
            <span class="cb-htext"><ng-container *ngTemplateOutlet="rich; context: { $implicit: b.title }"></ng-container></span>
            <span class="cb-count" *ngIf="b.count">{{ b.count }}</span>
            <span class="cb-note" *ngIf="b.note"><ng-container *ngTemplateOutlet="rich; context: { $implicit: b.note }"></ng-container></span>
          </header>
          <ul class="cb-rows">
            <li
              *ngFor="let it of b.items"
              [class.cb-row--action]="it.action"
              (click)="onRow(it)"
              [attr.role]="it.action ? 'button' : null"
              [attr.tabindex]="it.action ? 0 : null"
              (keydown.enter)="onRow(it)"
            >
              <span class="cb-dot"></span>
              <span class="cb-main">
                <span class="cb-name"><ng-container *ngTemplateOutlet="rich; context: { $implicit: it.title }"></ng-container></span>
                <span class="cb-meta" *ngIf="metaText(it)"> · <ng-container *ngTemplateOutlet="rich; context: { $implicit: metaText(it) }"></ng-container></span>
                <span class="cb-warn" *ngIf="it.warn"> ⚠ <ng-container *ngTemplateOutlet="rich; context: { $implicit: it.warn }"></ng-container></span>
              </span>
              <span class="cb-chip" *ngIf="it.badge" [ngClass]="chipClass(it.badgeKind)">{{ it.badge }}</span>
              <span class="cb-chip cb-chip--date" *ngIf="!it.badge && metaDate(it)">{{ metaDate(it) }}</span>
              <span class="cb-go" *ngIf="it.action" aria-hidden="true">›</span>
            </li>
          </ul>
          <div class="cb-foot" *ngIf="b.footer"><ng-container *ngTemplateOutlet="rich; context: { $implicit: b.footer }"></ng-container></div>
        </section>

        <!-- KPI -->
        <section *ngIf="b.type === 'kpi'" class="cb-card">
          <header class="cb-head" *ngIf="b.title"><span class="cb-htext"><ng-container *ngTemplateOutlet="rich; context: { $implicit: b.title }"></ng-container></span></header>
          <div class="cb-kpis">
            <div class="cb-kpi" *ngFor="let t of b.tiles" [class.cb-kpi--emph]="t.emphasis">
              <div class="cb-kpi-label"><ng-container *ngTemplateOutlet="rich; context: { $implicit: t.label }"></ng-container></div>
              <div class="cb-kpi-value"><ng-container *ngTemplateOutlet="rich; context: { $implicit: t.value }"></ng-container></div>
              <div class="cb-kpi-detail" *ngIf="t.detail"><ng-container *ngTemplateOutlet="rich; context: { $implicit: t.detail }"></ng-container></div>
            </div>
          </div>
        </section>

        <!-- RANKING -->
        <section *ngIf="b.type === 'ranking'" class="cb-card">
          <header class="cb-head" *ngIf="b.title">
            <span class="cb-htext"><ng-container *ngTemplateOutlet="rich; context: { $implicit: b.title }"></ng-container></span>
            <span class="cb-count" *ngIf="b.count">{{ b.count }}</span>
            <span class="cb-note" *ngIf="b.note"><ng-container *ngTemplateOutlet="rich; context: { $implicit: b.note }"></ng-container></span>
          </header>
          <ol class="cb-rank">
            <li *ngFor="let r of b.rows">
              <span class="cb-rank-no">{{ r.rank }}</span>
              <span class="cb-rank-name"><ng-container *ngTemplateOutlet="rich; context: { $implicit: r.name }"></ng-container></span>
              <span class="cb-bar" *ngIf="r.fraction != null"><span class="cb-bar-fill" [style.width.%]="r.fraction * 100"></span></span>
              <span class="cb-rank-val"><ng-container *ngTemplateOutlet="rich; context: { $implicit: r.value }"></ng-container></span>
            </li>
          </ol>
          <div class="cb-foot" *ngIf="b.footer"><ng-container *ngTemplateOutlet="rich; context: { $implicit: b.footer }"></ng-container></div>
        </section>

        <!-- COVERAGE -->
        <section *ngIf="b.type === 'coverage'" class="cb-card">
          <header class="cb-head" *ngIf="b.title"><span class="cb-htext"><ng-container *ngTemplateOutlet="rich; context: { $implicit: b.title }"></ng-container></span></header>
          <div class="cb-cov">
            <div class="cb-cov-row" *ngFor="let c of b.coverage">
              <span class="cb-cov-day"><ng-container *ngTemplateOutlet="rich; context: { $implicit: c.label }"></ng-container></span>
              <span class="cb-cov-track" *ngIf="c.percent >= 0">
                <span class="cb-cov-fill" [style.width.%]="c.percent"></span>
              </span>
              <span class="cb-cov-detail" *ngIf="c.percent < 0"><ng-container *ngTemplateOutlet="rich; context: { $implicit: c.detail }"></ng-container></span>
              <span class="cb-cov-pct" *ngIf="c.percent >= 0">
                {{ c.percent }}%<span class="cb-warn" *ngIf="c.critical"> ⚠</span>
              </span>
            </div>
          </div>
        </section>

        <!-- FIELDS -->
        <section *ngIf="b.type === 'fields'" class="cb-card">
          <header class="cb-head" *ngIf="b.title"><span class="cb-htext"><ng-container *ngTemplateOutlet="rich; context: { $implicit: b.title }"></ng-container></span></header>
          <dl class="cb-fields">
            <div class="cb-field" *ngFor="let f of b.fields">
              <dt><ng-container *ngTemplateOutlet="rich; context: { $implicit: f.label }"></ng-container></dt>
              <dd><ng-container *ngTemplateOutlet="rich; context: { $implicit: f.value }"></ng-container></dd>
            </div>
          </dl>
        </section>

        <!-- DELTA (Vertragspunkt 1) — Zeitvergleich: zwei Werte, Differenz, Richtung.
             Bis dieser Typ vom Backend kommt, ist ein Vergleich Fliesstext; der Renderer
             steht trotzdem schon, damit die Backend-Spur sofort wirkt. -->
        <section *ngIf="b.type === 'delta'" class="cb-card">
          <header class="cb-head" *ngIf="b.title"><span class="cb-htext"><ng-container *ngTemplateOutlet="rich; context: { $implicit: b.title }"></ng-container></span></header>
          <div class="cb-deltas">
            <div class="cb-delta" *ngFor="let d of b.deltas">
              <span class="cb-delta-label"><ng-container *ngTemplateOutlet="rich; context: { $implicit: d.label }"></ng-container></span>
              <span class="cb-delta-now"><ng-container *ngTemplateOutlet="rich; context: { $implicit: d.current }"></ng-container></span>
              <span class="cb-delta-was"><ng-container *ngTemplateOutlet="rich; context: { $implicit: d.previous }"></ng-container></span>
              <span class="cb-delta-diff" [ngClass]="deltaClass(d.direction)">
                <i [class]="deltaIcon(d.direction)" aria-hidden="true"></i><ng-container *ngTemplateOutlet="rich; context: { $implicit: d.deltaText }"></ng-container>
                <span class="cb-warn" *ngIf="d.critical"> ⚠</span>
              </span>
            </div>
          </div>
          <div class="cb-foot" *ngIf="b.footer"><ng-container *ngTemplateOutlet="rich; context: { $implicit: b.footer }"></ng-container></div>
        </section>

        <!-- CHOICE (Vertragspunkt 2) — Rueckfrage bei Mehrdeutigkeit.
             Antippen statt tippen: „Welche Kerstin meinst du?" als Liste MIT dem
             Unterscheidungsmerkmal (Abteilung), nicht als Textfrage. -->
        <section *ngIf="b.type === 'choice'" class="cb-card">
          <header class="cb-head" *ngIf="b.title"><span class="cb-htext"><ng-container *ngTemplateOutlet="rich; context: { $implicit: b.title }"></ng-container></span></header>
          <ul class="cb-rows cb-choices">
            <li
              *ngFor="let o of b.options"
              class="cb-row--action cb-choice"
              role="button"
              tabindex="0"
              (click)="onChoice(o)"
              (keydown.enter)="onChoice(o)"
            >
              <span class="cb-main">
                <span class="cb-name"><ng-container *ngTemplateOutlet="rich; context: { $implicit: o.title }"></ng-container></span>
                <span class="cb-meta" *ngIf="o.meta"> · <ng-container *ngTemplateOutlet="rich; context: { $implicit: o.meta }"></ng-container></span>
              </span>
              <span class="cb-go" aria-hidden="true">›</span>
            </li>
          </ul>
        </section>
      </ng-container>
    </div>

    <!-- Inline-Fettung (**…**) als <strong>, ohne innerHTML: Angular maskiert jedes Stueck. -->
    <ng-template #rich let-text>
      <ng-container *ngFor="let seg of segments(text)"
        ><strong *ngIf="seg.bold">{{ seg.text }}</strong
        ><ng-container *ngIf="!seg.bold">{{ seg.text }}</ng-container></ng-container
      >
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
        align-self: flex-start;
        width: 96%;
        max-width: 96%;
        /* Kompakte Basis-Schrift wie die Chat-Bubbles — sonst erbt die Karte die groessere
           globale Schrift und Namen wirken klobig. Alle Karten-Texte skalieren relativ dazu. */
        font-size: var(--fs-sm);
        line-height: 1.4;
      }
      .cb {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin: 6px 0;
      }
      .cb-card {
        background: var(--clr-surface-100, #fff);
        border: 1px solid var(--clr-border, #e4e8ee);
        border-radius: 13px;
        padding: 11px 13px;
        box-shadow: 0 1px 2px rgba(16, 33, 51, 0.05);
      }
      .cb-head {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 7px;
        margin-bottom: 6px;
        font-size: var(--fs-base);
        font-weight: 700;
        color: var(--clr-text-100, #1d2733);
      }
      .cb-count {
        font-size: var(--fs-xs);
        font-weight: 700;
        line-height: 1.7;
        padding: 0 8px;
        border-radius: 999px;
        font-variant-numeric: tabular-nums;
        background: rgba(var(--clr-primary-100-rgb, 76, 202, 241), 0.16);
        color: var(--ui-primary, #0284c7);
      }
      .cb-note {
        font-size: var(--fs-xs);
        font-weight: 500;
        color: var(--clr-text-300, #94a1ad);
      }
      /* Rows (section) */
      .cb-rows {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 1px;
      }
      .cb-rows li {
        display: flex;
        align-items: baseline;
        gap: 8px;
        padding: 4px 6px;
        border-radius: 8px;
      }
      .cb-row--action {
        cursor: pointer;
        /* A3: eine antippbare Zeile ist ein Bedienelement und braucht das volle Ziel.
           Echte Mindesthoehe statt Ausleger — die Zeilen stehen dicht gestapelt, ein
           Ueberstand faenge dem Nachbarn die Klicks ab. */
        min-height: var(--touch-min);
        align-items: center;
      }
      .cb-row--action:hover,
      .cb-row--action:focus-visible {
        background: var(--clr-surface-200, #f4f5f8);
        outline: none;
      }
      .cb-dot {
        flex: 0 0 auto;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: rgba(var(--clr-primary-100-rgb, 76, 202, 241), 0.6);
        transform: translateY(-2px);
      }
      .cb-main {
        flex: 1;
        min-width: 0;
      }
      .cb-name {
        font-weight: 600;
        color: var(--clr-text-100, #1d2733);
      }
      .cb-meta {
        color: var(--clr-text-300, #94a1ad);
        font-size: var(--fs-xs);
      }
      .cb-warn {
        font-weight: 600;
        color: var(--clr-text-100, #1d2733);
      }
      .cb-go {
        flex: 0 0 auto;
        color: var(--clr-text-300, #94a1ad);
        font-weight: 700;
      }
      .cb-chip {
        flex: 0 0 auto;
        align-self: center;
        font-size: var(--fs-xs);
        font-weight: 700;
        line-height: 1.6;
        padding: 0 8px;
        border-radius: 999px;
        white-space: nowrap;
        text-transform: capitalize;
      }
      .cb-chip--urgent {
        background: var(--ui-primary, #0284c7);
        color: #fff;
      }
      .cb-chip--high {
        background: transparent;
        color: var(--ui-primary, #0284c7);
        box-shadow: inset 0 0 0 1px rgba(var(--ui-primary-rgb, 2, 132, 199), 0.5);
      }
      .cb-chip--date,
      .cb-chip--neutral {
        background: var(--clr-surface-300, #eef1f4);
        color: var(--clr-text-200, #55606e);
        text-transform: none;
        font-weight: 600;
      }
      .cb-foot {
        margin-top: 6px;
        padding-top: 6px;
        font-size: var(--fs-xs);
        color: var(--clr-text-300, #94a1ad);
        border-top: 1px solid var(--clr-border, #e4e8ee);
      }
      /* KPI */
      .cb-kpis {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 7px;
      }
      .cb-kpi {
        background: var(--clr-surface-200, #f4f5f8);
        border: 1px solid var(--clr-border, #e4e8ee);
        border-radius: 11px;
        padding: 8px 11px;
      }
      .cb-kpi--emph {
        border-left: 3px solid var(--ui-primary, #0284c7);
      }
      .cb-kpi-label {
        font-size: var(--fs-xs);
        font-weight: 600;
        color: var(--clr-text-300, #94a1ad);
      }
      .cb-kpi-value {
        font-size: 19px;
        font-weight: 700;
        line-height: 1.25;
        font-variant-numeric: tabular-nums;
        color: var(--clr-text-100, #1d2733);
      }
      .cb-kpi-detail {
        font-size: var(--fs-xs);
        color: var(--clr-text-200, #55606e);
      }
      /* Ranking */
      .cb-rank {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 3px;
      }
      .cb-rank li {
        display: flex;
        align-items: center;
        gap: 9px;
        padding: 2px 2px;
      }
      .cb-rank-no {
        flex: 0 0 auto;
        width: 19px;
        height: 19px;
        display: grid;
        place-items: center;
        border-radius: 6px;
        font-size: var(--fs-xs);
        font-weight: 700;
        background: rgba(var(--clr-primary-100-rgb, 76, 202, 241), 0.16);
        color: var(--ui-primary, #0284c7);
      }
      .cb-rank-name {
        flex: 1 1 auto;
        min-width: 0;
        font-weight: 600;
        color: var(--clr-text-100, #1d2733);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .cb-bar {
        flex: 0 0 78px;
        height: 6px;
        border-radius: 4px;
        background: var(--clr-surface-300, #eef1f4);
        overflow: hidden;
      }
      .cb-bar-fill {
        display: block;
        height: 100%;
        background: linear-gradient(90deg, var(--clr-primary-100, #4ccaf1), var(--clr-primary-200, #3e95be));
      }
      .cb-rank-val {
        flex: 0 0 auto;
        font-variant-numeric: tabular-nums;
        font-weight: 600;
        color: var(--clr-text-200, #55606e);
      }
      /* Coverage */
      .cb-cov {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .cb-cov-row {
        display: flex;
        align-items: center;
        gap: 9px;
      }
      .cb-cov-day {
        flex: 0 0 78px;
        font-size: var(--fs-xs);
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        color: var(--clr-text-100, #1d2733);
      }
      .cb-cov-track {
        flex: 1 1 auto;
        height: 8px;
        border-radius: 5px;
        background: var(--clr-surface-300, #eef1f4);
        overflow: hidden;
      }
      .cb-cov-fill {
        display: block;
        height: 100%;
        background: linear-gradient(90deg, var(--clr-primary-100, #4ccaf1), var(--clr-primary-200, #3e95be));
      }
      .cb-cov-detail {
        flex: 1 1 auto;
        font-size: var(--fs-xs);
        color: var(--clr-text-300, #94a1ad);
      }
      .cb-cov-pct {
        flex: 0 0 auto;
        width: 60px;
        text-align: right;
        font-size: var(--fs-xs);
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: var(--clr-text-200, #55606e);
      }
      .cb-cov-legend {
        margin-top: 7px;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .cb-cov-leg-item {
        font-size: var(--fs-xs);
        color: var(--clr-text-300, #94a1ad);
      }
      /* Fields */
      .cb-fields {
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 3px;
      }
      .cb-field {
        display: flex;
        gap: 10px;
        align-items: baseline;
      }
      .cb-field dt {
        flex: 0 0 130px;
        font-size: var(--fs-xs);
        color: var(--clr-text-300, #94a1ad);
      }
      .cb-field dd {
        margin: 0;
        flex: 1;
        color: var(--clr-text-100, #1d2733);
      }
      /* Delta (Zeitvergleich) — Beschriftung, aktueller Wert, Vorwert, Differenz.
         Zwei Zeilen: oben Beschriftung und Differenz, darunter die zwei Werte. So bleibt
         die Aussage („+12 %") auf 390px links vom Rand und nicht in einer vierten Spalte. */
      .cb-deltas {
        display: flex;
        flex-direction: column;
        gap: var(--sp-2);
      }
      .cb-delta {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 2px var(--sp-2);
        align-items: baseline;
      }
      .cb-delta-label {
        grid-column: 1;
        font-weight: 600;
        color: var(--clr-text-100, #1d2733);
      }
      .cb-delta-diff {
        grid-column: 2;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: var(--fs-xs);
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
      /* Neutral eingefaerbt: Ob „mehr" gut ist, entscheidet die Kennzahl, nicht der Pfeil.
         Die Hervorhebung traegt critical (⚠), nicht die Farbe. */
      .cb-delta-diff--up,
      .cb-delta-diff--down,
      .cb-delta-diff--flat {
        color: var(--clr-text-200, #55606e);
      }
      .cb-delta-now {
        grid-column: 1;
        grid-row: 2;
        font-size: var(--fs-md);
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: var(--clr-text-100, #1d2733);
      }
      .cb-delta-was {
        grid-column: 2;
        grid-row: 2;
        font-size: var(--fs-xs);
        font-variant-numeric: tabular-nums;
        color: var(--clr-text-300, #94a1ad);
        text-align: right;
      }

      /* Choice (Rueckfrage) — dieselbe Zeilenoptik wie section, aber IMMER antippbar. */
      .cb-choice {
        border: 1px solid var(--clr-border, #e4e8ee);
        margin-bottom: 4px;
        padding-inline: var(--sp-3);
      }

      /* Dark-Mode: Badges/Balken auf dunkler Flaeche anheben. */
      :host-context(.theme-dark) .cb-count,
      :host-context(.theme-dark) .cb-rank-no {
        background: rgba(var(--clr-primary-100-rgb, 87, 209, 247), 0.22);
        color: var(--clr-primary-100, #57d1f7);
      }
      :host-context(.theme-dark) .cb-chip--urgent {
        background: var(--clr-primary-100, #57d1f7);
        color: #0b2230;
      }
      :host-context(.theme-dark) .cb-chip--high {
        color: var(--clr-primary-100, #57d1f7);
        box-shadow: inset 0 0 0 1px rgba(var(--clr-primary-100-rgb, 87, 209, 247), 0.55);
      }
    `,
  ],
})
export class ChatBlocksComponent {
  @Input() blocks: ChatBlock[] = [];
  @Output() rowAction = new EventEmitter<{ action: string; arg: string }>();

  protected segments(text: unknown): InlineSegment[] {
    return inlineBoldSegments(text);
  }

  protected chipClass(kind?: string): string {
    switch (kind) {
      case 'urgent':
        return 'cb-chip--urgent';
      case 'high':
        return 'cb-chip--high';
      case 'date':
        return 'cb-chip--date';
      default:
        return 'cb-chip--neutral';
    }
  }

  /**
   * Zerlegt die Meta-Zeile in (Text, Datum): ein abschliessendes „… ab DD.MM.YYYY" wird als
   * rechtsbuendiges Datums-Chip dargestellt statt inline — das entlastet dichte Zeilen (z. B.
   * offene Schichten mit Abteilung + Qualifikation + Datum). Greift nur, wenn kein anderes Badge
   * (z. B. Prioritaet) gesetzt ist.
   */
  private splitMetaDate(meta?: string): { text: string; date: string | null } {
    const m = (meta ?? '').match(/^(.*?)[\s·]*\bab\s+(\d{1,2}\.\d{1,2}\.\d{2,4})\s*$/);
    return m ? { text: m[1].replace(/[\s·]+$/, ''), date: 'ab ' + m[2] } : { text: meta ?? '', date: null };
  }

  protected metaText(item: { meta?: string; badge?: string }): string {
    return item.badge ? item.meta ?? '' : this.splitMetaDate(item.meta).text;
  }

  protected metaDate(item: { meta?: string }): string | null {
    return this.splitMetaDate(item.meta).date;
  }

  protected onRow(item: { action?: string; actionArg?: string }): void {
    if (item.action) {
      this.rowAction.emit({ action: item.action, arg: item.actionArg ?? '' });
    }
  }

  /**
   * Auswahl bei einer Rueckfrage. `actionArg` ist der eindeutige Bezeichner (z. B. der
   * volle Name mit Abteilung); fehlt er, dient der Titel als Rueckfall — eine Option, die
   * nichts ausloest, waere eine zweite Sackgasse.
   */
  protected onChoice(option: { title: string; action?: string; actionArg?: string }): void {
    this.rowAction.emit({
      action: option.action || 'disambiguate',
      arg: option.actionArg || option.title,
    });
  }

  /** Pfeil nach Richtung — bei unbekannter Richtung ein neutraler Strich, keine Behauptung. */
  protected deltaIcon(direction: 'up' | 'down' | 'flat'): string {
    if (direction === 'up') return 'fa-solid fa-arrow-trend-up';
    if (direction === 'down') return 'fa-solid fa-arrow-trend-down';
    return 'fa-solid fa-minus';
  }

  /**
   * Klasse fuer die Differenz.
   *
   * ⚠️ Bewusst nach RICHTUNG benannt (`--up`/`--down`), nicht nach Wertung (`--good`/`--bad`):
   * Ob „mehr" gut ist, haengt an der Kennzahl — mehr Besetzung ja, mehr Überstunden nein.
   * Die Wertung traegt `critical`, nicht der Pfeil.
   */
  protected deltaClass(direction: 'up' | 'down' | 'flat'): string {
    return `cb-delta-diff--${direction}`;
  }
}
